import { alertVariants } from "@visiblethread/react-components";
import localhost, { downloadFileFromResponse } from "apis/localhost";
import { systemAdminImportUsersFromCsvConfirmationModal } from "components/Modal/confModalTypes";
import { MULTIPART_FORM_DATA_CONTENT_TYPE } from "constants/app";
import { modalTypes } from "constants/types";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal, showModal } from "slices/app/actions/modals";
import { pageLoading, showGlobalAlert } from "slices/app/appSlice";
import {
  setUsersInfo,
  setWorkspacesInfo,
} from "slices/systemAdmin/systemAdminSlice";

export const fetchUsers = () => async (dispatch, getState) => {
  dispatch(pageLoading(true));
  const { adminType } = getState().systemAdmin;

  await localhost
    .get(`/${adminType}/users`)
    .then(async (response) => {
      const { data } = response;
      dispatch(setUsersInfo(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};

export const createUser =
  (userName, firstName, lastName, password, systemAdmin) =>
  async (dispatch) => {
    dispatch(pageLoading(true));
    await localhost
      .post("/systemAdmin/user", {
        userName,
        firstName,
        lastName,
        password,
        systemAdmin,
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(setUsersInfo(data));
        dispatch(hideModal(modalTypes.NEW_USER));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: "User Created Successfully",
          })
        );
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.NEW_USER));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const createUserWithWorkspace =
  (
    userName,
    firstName,
    lastName,
    password,
    workspaceId,
    licensed,
    readabilitySettingsAdmin,
    acronymSettingsAdmin,
    discoverySettingsAdmin,
    excelOutputsAdmin,
    proposalStagesAdmin,
    dictionaryAdmin,
    docStyleTemplatesAdmin,
    editCurrenciesAdmin,
    archiveStatusesAdmin,
    editCustomFieldsAdmin,
    shreddingTemplatesAdmin,
    customCategoriesAdmin,
    admin
  ) =>
  async (dispatch, getState) => {
    dispatch(pageLoading(true));
    const { adminType } = getState().systemAdmin;
    await localhost
      .post(`/${adminType}/user/workspace`, {
        userName,
        firstName,
        lastName,
        password,
        workspaceId,
        licensed,
        readabilitySettingsAdmin,
        acronymSettingsAdmin,
        discoverySettingsAdmin,
        excelOutputsAdmin,
        proposalStagesAdmin,
        dictionaryAdmin,
        docStyleTemplatesAdmin,
        editCurrenciesAdmin,
        archiveStatusesAdmin,
        editCustomFieldsAdmin,
        shreddingTemplatesAdmin,
        customCategoriesAdmin,
        admin,
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(setWorkspacesInfo(data));
        dispatch(hideModal(modalTypes.NEW_USER));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: "User Created Successfully",
          })
        );
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.NEW_USER));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const editUser =
  (userName, firstName, lastName, password, systemAdmin) =>
  async (dispatch) => {
    dispatch(pageLoading(true));
    await localhost
      .patch("/systemAdmin/user", {
        userName,
        firstName,
        lastName,
        password,
        systemAdmin,
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(setUsersInfo(data));
        dispatch(hideModal(modalTypes.EDIT_USER));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: "User Successfully Updated",
          })
        );
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.EDIT_USER));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const deleteUser = (username) => async (dispatch) => {
  dispatch(pageLoading(true));
  await localhost
    .delete("/systemAdmin/user", {
      params: { username },
    })
    .then(async (response) => {
      const { data } = response;
      dispatch(setUsersInfo(data));
      dispatch(hideModal(modalTypes.CONFIRMATION));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: `User ${username} successfully deleted from the system`,
        })
      );
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.EDIT_USER));
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};

export const exportActivityReport = (filter) => async (dispatch) => {
  dispatch(pageLoading(true));

  await localhost
    .get("/systemAdmin/activity", {
      responseType: "blob",
      params: { filter },
    })
    .then(async (response) => {
      const { data } = response;

      downloadFileFromResponse(data, "ActivityReport.csv");

      dispatch(hideModal(modalTypes.EXPORT_REPORT));
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.EXPORT_REPORT));
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};

export const importUsersFromCsv = (formData) => async (dispatch, getState) => {
  dispatch(pageLoading(true));
  const { adminType } = getState().systemAdmin;

  await localhost
    .post(`/${adminType}/importUsers`, formData, {
      headers: {
        "Content-Type": MULTIPART_FORM_DATA_CONTENT_TYPE,
      },
    })
    .then(async (response) => {
      const { data } = response;
      if (response.status === 200) {
        dispatch(setUsersInfo(data.usersResponse));
      }
      dispatch(hideModal(modalTypes.IMPORT_USERS));
      dispatch(
        showModal(
          modalTypes.CONFIRMATION,
          systemAdminImportUsersFromCsvConfirmationModal(
            data.usersResultString,
            data.message,
            dispatch
          )
        )
      );
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.IMPORT_USERS));
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};

export const exportResultsImportUsersFromCsv =
  (results) => async (dispatch, getState) => {
    dispatch(pageLoading(true));
    const { adminType } = getState().systemAdmin;

    await localhost
      .get(`/${adminType}/exportUsersResult`, {
        responseType: "blob",
        params: { usersString: results },
      })
      .then(async (response) => {
        const { data } = response;

        downloadFileFromResponse(data, "BulkUserResults.csv");

        dispatch(hideModal(modalTypes.CONFIRMATION));
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.CONFIRMATION));
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const exportUserList = () => async (dispatch) => {
  dispatch(pageLoading(true));

  await localhost
    .get("/systemAdmin/exportUserList", {
      responseType: "blob",
      params: {},
    })
    .then(async (response) => {
      const { data } = response;

      downloadFileFromResponse(data, "UserList.csv");

      dispatch(hideModal(modalTypes.CONFIRMATION));
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.CONFIRMATION));
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};
export const userLogOut = (username) => async (dispatch, getState) => {
  dispatch(pageLoading(true));
  const { adminType } = getState().systemAdmin;

  await localhost
    .post(`/${adminType}/logout`, null, {
      params: { username },
    })
    .then(async () => {
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: "User logged out successfully",
        })
      );
      dispatch(fetchUsers());
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(pageLoading(false));
    });
};
